import React from "react";
import * as Sentry from "@sentry/react";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const BENIGN_DOM_ERRORS = [
  "removeChild",
  "insertBefore",
  "The node to be removed is not a child",
  "Failed to execute 'removeChild'",
  "Failed to execute 'insertBefore'",
];

function isBenignDomError(error: Error | null): boolean {
  if (!error) return false;
  const msg = error.message || "";
  return BENIGN_DOM_ERRORS.some((s) => msg.includes(s));
}

/** Após novo deploy, o bundle antigo referencia chunks com hash que não existem mais (404 / fetch falha). */
export const STORAGE_KEY_TENTATIVAS_CHUNK_DEPLOY = "ferrugini_chunk_attempt";

function isStaleLazyChunkError(error: Error | null): boolean {
  if (!error) return false;
  const msg = error.message || "";
  if (msg.includes("Failed to fetch dynamically imported module")) return true;
  if (msg.includes("Importing a module script failed")) return true;
  if (error.name === "ChunkLoadError") return true;
  return false;
}

function tryReloadForStaleChunk(): boolean {
  try {
    const n = Number(sessionStorage.getItem(STORAGE_KEY_TENTATIVAS_CHUNK_DEPLOY) || "0");
    if (n >= 2) return false;
    sessionStorage.setItem(STORAGE_KEY_TENTATIVAS_CHUNK_DEPLOY, String(n + 1));
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Don't crash for benign DOM manipulation errors (browser extensions)
    if (isBenignDomError(error)) {
      console.warn("[ErrorBoundary] Suppressed benign DOM error:", error.message);
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (isBenignDomError(error)) {
      console.warn("[ErrorBoundary] Suppressed benign DOM error in componentDidCatch:", error.message);
      return;
    }
    if (isStaleLazyChunkError(error) && tryReloadForStaleChunk()) {
      return;
    }
    console.error("[ErrorBoundary]", error, errorInfo);
    Sentry.captureReactException(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-md w-full text-center space-y-4">
            <h1 className="text-2xl font-bold text-foreground">Ocorreu um erro técnico na plataforma Ferrugini</h1>
            <p className="text-muted-foreground text-sm">
              Nossa equipe já foi notificada. Por favor, tente recarregar a página ou retorne em instantes.
            </p>
            {this.state.error && (
              <details className="text-left bg-muted/50 rounded p-3 text-xs text-muted-foreground">
                <summary className="cursor-pointer font-medium">Detalhes do erro</summary>
                <pre className="mt-2 whitespace-pre-wrap break-words">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <button
              onClick={this.handleReload}
              className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90"
            >
              Recarregar página
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
